<template>
	<div class="invoices-table">
		<AppTable main-color="#6c33cb" ref="invoices-table"
				  :fetch-function="getFilteredInvoices"
				  :filters="filters"
				  :table-headings="tableHeadings"
				  :title="getString('invoices')"
				  :total-items="businessInvoicesTotal || 0"
				  :items="businessInvoices || []"
				  initial-sort-field="date"
				  @failed-fetch="details=>{$emit('failed-fetch', details)}">

			<template #url="{item}">
				<div class="row centered">
					<AppTableButtonGroup v-if="isSuper && isFailedState(item.state)" :buttons="buttonsActions" :item="item"
										 @retry-invoice="emitInvoice(item)" @picked-send-manual="manualInvoice(item)"/>
					<AppTableButtonGroup v-else-if="isSuper && canEmitCreditNote(item)" :buttons="buttonsCreditNote" :item="item"
										 @emit-credit-note="showCreditNoteConfirmation(item)" @picked-download="openUrl(item.url)"/>
					<AppTableButtonGroup v-else-if="!!item.url" :buttons="buttonsDownload" :item="item" @picked-download="openUrl(item.url)"/>
					<div class="no-download" v-else>N/A</div>
				</div>
			</template>

		</AppTable>
		<AppNotification ref="failed-invoice" :title="getString('error')" :body-text="getString('failed_invoice')" :type="-2" />
		<AppNotification ref="success-invoice" :title="getString('success')" :body-text="getString('operation_completed')" :type="1" />
		<AppNotification :title="getString('error')" :body-text="getString('generic_error')" :type="-2" ref="generic-error" />
		<AppPopup ref="credit-note-confirm" :title="getString('emit_credit_note')"
				  :body-text="getString('credit_note_confirm_message')"
				  :buttons="creditNotePopupButtons"/>
	</div>
</template>

<script>
import API from "@/shared/helpers/API";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppTable from "@/shared/components/AppTable.vue";
import {useLanguageStore} from "@/shared/stores";
import {getLocalDateFromServer} from "@/shared/helpers/dates";
import AppNotification from "@/shared/components/AppNotification.vue";
import AppTableButtonGroup from "@/shared/components/AppTableButtonGroup.vue";
import AppPopup from "@/shared/components/AppPopup.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		return {getString: languageStore.getString, languageStore}
	},
	name: "InvoicesTable",
	components: {AppTableButtonGroup, AppNotification, AppButtonIcon, AppTable, AppPopup},
	props: {
		isSuper: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			loadingInvoice: false,
			businessInvoicesTotal: 0,
			businessInvoices: [],
			creditNoteInvoice: null,
			filters: [
				{
					type: "date",
					key: 'date',
					tableName: 'businesses_invoices',
					text: this.getString('date'),
					finalValue: '',
					active: true
				},
				{
					type: "price",
					key: "amount",
					text: this.getString('amount'),
					active: false,
					value: null,
					activeItem: 0,
					finalValue: '',
				},
				{
					type: "checklist",
					key: "state",
					text: this.getString('status'),
					values: ['failed', 'refused', 'normal', 'electronic', 'manual', 'confirmed', 'refund_requested', 'refund_failed', 'refunded'].map(t => this.getString('state_'+t)),
					keysArray: ['failed', 'refused', 'normal', 'electronic', 'manual', 'confirmed', 'refund_requested', 'refund_failed', 'refunded'],
					active: false,
					selectedIndexes: [],
				},
				{
					type: "exactMatch",
					key: "business_id",
					text: this.getString('business_id'),
					placeholder: this.getString('business_id'),
					active: false,
					value: null,
					finalValue: '',
				},
				{
					type: "checklist",
					key: "type",
					text: this.getString('origin'),
					values: ['commissions_stripe', 'commissions_pos', 'commissions_other', 'commissions_payout', 'invoice_type_shop', 'super_invoice', 'subscription', 'dispute_management_fee', 'recharge_credit'].map(t => this.getString(t)),
					keysArray: ['commissions_stripe', 'commissions_pos', 'commissions_other', 'commissions_payout', 'shop', 'super_invoice', 'subscription', 'dispute_management_fee', 'recharge_credit'],
					selectedIndexes: [],
				},
				{
					type: "checklist",
					key: "document_type",
					text: this.getString('type'),
					values: ['invoice', 'credit_note'].map(t => this.getString(t)),
					keysArray: ['invoice', 'credit_note'],
					selectedIndexes: [],
				},
			],
			tableHeadings: [
				{
					text: this.getString('date'),
					cols: 1,
					sortable: true,
					active: true,
					sortAsc: false,
					fieldName: 'date',
				},
				{
					text: this.getString('amount'),
					cols: 1,
					sortable: true,
					fieldName: 'amount',
				},
				{
					text: "Business ID",
					cols: 1,
					sortable: true,
					fieldName: 'business_id_string',
					sortFieldName: 'business_id',
				},
				{
					text: this.getString('origin'),
					cols: 1,
					fieldName: 'origin_readable',
				},
				{
					text: this.getString('type'),
					cols: 1,
					fieldName: 'type_readable',
				},
				{
					text: this.getString('status'),
					cols: 1,
					fieldName: 'state_readable',
				},
				{
					text: this.getString('error'),
					fieldName: 'error',
					cols: 2,
				},
				{
					text: this.getString('description'),
					fieldName: 'description',
					cols: 2,
				},
				{
					text: this.isSuper ? this.getString('action') : this.getString('download'),
					cols: 1,
					fieldName: 'url',
					slotName: 'url',
				},
			],
		}
	},
	computed: {
		buttonsActions() {
			return [
				{
					iconName: "icon-refund-blue-new",
					class: "outline",
					emitName: "retry-invoice",
					loading: this.loadingInvoice,
					title: this.getString('retry_emit')
				},
				{
					iconName: "icon-send-manual-blue",
					emitName: "picked-send-manual",
					loading: this.loadingInvoice,
					title: this.getString('manually_emitted')
				}
			];
		},
		buttonsDownload() {
			return [
				{
					iconName: "icon-download-blue",
					emitName: "picked-download",
					title: this.getString('download'),
				}
			];
		},
		buttonsCreditNote() {
			return [
				{
					iconName: "icon-download-blue",
					emitName: "picked-download",
					title: this.getString('download'),
				},
				{
					iconName: "icon-refund-blue-new",
					emitName: "emit-credit-note",
					loading: this.loadingInvoice,
					title: this.getString('emit_credit_note')
				}
			];
		},
		creditNotePopupButtons() {
			return [
				{text: this.getString('cancel'), negative: true, callback: () => this.cancelCreditNote()},
				{text: this.getString('confirm'), callback: () => this.confirmCreditNote()}
			];
		},
	},
	methods: {
		isFailedState(state) {
			return state === 'failed' || state === 'refused';
		},
		getFilteredInvoices(filters, callback = null) {

			API.init().getSuperInvoices(JSON.stringify(filters || []), r=> {
				this.businessInvoices = [];
				this.businessInvoicesTotal = 0;
				if(r && r.result === "OK") {
					if(r.invoices && r.invoices.selected && r.invoices.selected.length) {
						r.invoices.selected.forEach(t => {
							this.businessInvoices.push(this.normalizeInvoice(t));
						});
						this.businessInvoicesTotal = Number(r.invoices.selected_num || 0);
					}
					if(callback) { callback(true); }
				} else {
					this.$refs["generic-error"].openNotification();
					if(callback) { callback(false, r ? r.details : null); }
				}
			});

		},
		normalizeInvoice(invoice) {
			invoice.date = invoice.date ? getLocalDateFromServer(invoice.date) : null;
			invoice.amount = Number(invoice.amount || 0);
			invoice.state_readable = this.languageStore.getString("state_"+invoice.state);
			if(invoice.reference_invoice_id && ['failed', 'electronic', 'confirmed'].includes(invoice.state)) {
				switch (invoice.state) {
					case 'failed': 		invoice.state_readable = this.languageStore.getString("state_refund_failed");		break;
					case 'electronic':	invoice.state_readable = this.languageStore.getString("credit_note_requested");	break;
					case 'confirmed':	invoice.state_readable = this.languageStore.getString("credit_note_issued");		break;
				}
			}
			invoice.business_id_string = invoice.business_id ? "#"+invoice.business_id : "-";
			invoice.error = invoice.error || "-";
			invoice.origin_readable = invoice.type ? this.languageStore.getString("invoice_type_"+invoice.type) : "-";
			invoice.type_readable = this.languageStore.getString(invoice?.reference_invoice_id ? "credit_note" : "invoice");
			return invoice;
		},
		emitInvoice(invoice) {

			if(this.loadingInvoice) {
				return;
			}
			this.loadingInvoice = true;

			API.init().emitInvoiceAgain(invoice.business_invoice_id, r=> {
				this.loadingInvoice = false;
				if(r && r.result === "OK") {
					this.$refs["success-invoice"].openNotification();
					invoice.state = r.state;
					invoice.state_readable = this.languageStore.getString("state_"+invoice.state);
					if(invoice.reference_invoice_id && ['failed', 'electronic', 'confirmed'].includes(invoice.state)) {
						switch (invoice.state) {
							case 'failed': 		invoice.state_readable = this.languageStore.getString("state_refund_failed");		break;
							case 'electronic':	invoice.state_readable = this.languageStore.getString("credit_note_requested");	break;
							case 'confirmed':	invoice.state_readable = this.languageStore.getString("credit_note_issued");		break;
						}
					}
					invoice.error = r.error || "-";
					invoice.url = r.url || null;
				} else {
					this.$refs["failed-invoice"].openNotification();
				}
			});

		},
		manualInvoice(invoice) {

			if(this.loadingInvoice) {
				return;
			}
			this.loadingInvoice = true;

			API.init().emitInvoiceManual(invoice.business_invoice_id, r=> {
				this.loadingInvoice = false;
				if(r && r.result === "OK") {
					this.$refs["success-invoice"].openNotification();
					invoice.state = r.state;
				} else {
					this.$refs["failed-invoice"].openNotification();
				}
			});

		},
		openUrl(url) {
			if(!url) { return }
			window.open(url, '_blank');
		},
		filterFailedInvoices() {
			let stateFilter = this.filters.find(f => f.key === 'state');
			if (stateFilter) {
				stateFilter.active = true;
				stateFilter.selectedIndexes = [0, 1];
				stateFilter.finalValue = stateFilter.values[0] + ', ' + stateFilter.values[1];
			}
			if (this.$refs['invoices-table']) {
				this.$refs['invoices-table'].changedFilter({state: ['failed', 'refused']});
			}
		},
		canEmitCreditNote(item) {
			// Can emit credit note if: confirmed state, has apifattura_invoice_id, and not already a credit note
			return item.state === 'confirmed' && item.apifattura_invoice_id && !item.reference_invoice_id;
		},
		showCreditNoteConfirmation(invoice) {
			this.creditNoteInvoice = invoice;
			this.$refs["credit-note-confirm"].toggleStatus(true);
		},
		cancelCreditNote() {
			this.creditNoteInvoice = null;
			this.$refs["credit-note-confirm"].toggleStatus(false);
		},
		confirmCreditNote() {
			if(this.loadingInvoice || !this.creditNoteInvoice) {
				return;
			}
			this.loadingInvoice = true;
			this.$refs["credit-note-confirm"].toggleStatus(false);

			const invoice = this.creditNoteInvoice;
			API.init().superEmitCreditNote(invoice.business_invoice_id, r => {
				this.loadingInvoice = false;
				this.creditNoteInvoice = null;
				if(r && r.result === "OK") {
					this.$refs["success-invoice"].openNotification();
					invoice.state = 'refund_requested';
					invoice.state_readable = this.languageStore.getString("state_refund_requested");
				} else {
					this.$refs["failed-invoice"].openNotification();
				}
			});
		},
	}

}
</script>

<style lang="scss" scoped>
.invoices-table {
	width: 100%;
}
.link-download:after {
	display: none;
}
.super-actions {
	display: flex;
	justify-content: center;
	align-items: center;
}
.no-download {
	font-size: 12px;
}
</style>